import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';

const preventDismiss = (e: Event) => e.preventDefault();

interface SessionOverlayProps {
  /** Whether the overlay is visible */
  open: boolean;
  /** Dialog title text */
  title: string;
  /** Dialog description text */
  description: string;
  /** Optional action button (e.g., "Log in" link) */
  action?: React.ReactNode;
}

/**
 * Full-screen non-dismissible modal overlay for session-related states
 * (expired session, explicit logout, etc.).
 */
export function SessionOverlay({ open, title, description, action }: SessionOverlayProps) {
  return (
    <Dialog.Root open={open}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'z-modal-backdrop fixed inset-0',
            'bg-black/60 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <Dialog.Content
          className={cn(
            'z-modal fixed left-1/2 top-1/2',
            '-translate-x-1/2 -translate-y-1/2',
            'w-full max-w-sm',
            'bg-card rounded-lg shadow-lg',
            'focus:outline-none',
            'flex flex-col items-center p-8 text-center',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
          )}
          onEscapeKeyDown={preventDismiss}
          onPointerDownOutside={preventDismiss}
          onInteractOutside={preventDismiss}
        >
          <Dialog.Title className="text-foreground mb-2 text-lg font-semibold">
            {title}
          </Dialog.Title>

          <Dialog.Description className={cn('text-muted-foreground text-sm', action && 'mb-6')}>
            {description}
          </Dialog.Description>

          {action}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
